// src/hooks/useResolvedShipping.js
//
// Resuelve el envío REAL del carrito actual contra /shipping/calculate, la misma
// fuente de verdad que usa el checkout. Sirve para que el sidecart y el carrito
// pinten la barra de envío gratis con los mismos datos que el resumen de compra:
//
//   - cost === 0        -> el envío ya es gratis (por monto de la zona O por regla de
//                          producto, p.ej. free_shipping_from_rule). Barra completa.
//   - threshold         -> el min_order_amount de LA ZONA del cliente (no el mínimo global).
//                          null => la zona NO tiene método free_shipping (sólo flat_rate),
//                          es decir el envío gratis es IMPOSIBLE ahí: hay que ocultar la barra.
//   - methodName        -> 'La Negra' | 'Alas' | ... (define si la barra aplica).
//
// Dirección usada, en orden:
//   1. La que el cliente seleccionó/ingresó en el checkout (coords persistidas allí).
//   2. Su dirección principal (main === 1).
// Si no hay ninguna (p.ej. invitado sin dirección), devuelve ready:false y el llamador
// cae a su comportamiento anterior.

import { useContext, useEffect, useState } from 'react';
import axios from 'axios';
import { MyContext } from '../context/AppContext';
import { mapProductsForShipping, shippingRequestConfig } from '../utils/shippingPayload';
import { readApplyUserDiscount } from './useUserDiscountPreference';
import { getCustomerEmail } from '../utils/customerEmail';

// pending = hay dirección y carrito, así que la resolución viene en camino: el llamador
// debe esperarla en vez de adivinar con el umbral global (si no, la barra parpadearía
// "gratis" durante un instante en zonas de costo fijo).
const EMPTY = { ready: false, pending: false, cost: null, threshold: null, methodName: null };
const PENDING = { ...EMPTY, pending: true };
const DEBOUNCE_MS = 350; // el carrito cambia seguido (cantidades); evitamos spamear la API

const readSelectedCoords = () => {
  if (typeof localStorage === 'undefined') return null;
  try {
    const parsed = JSON.parse(localStorage.getItem('selected_shipping_coords'));
    if (parsed && Number(parsed.lat) && Number(parsed.lng)) return parsed;
  } catch (e) {
    // JSON inválido: se ignora y se usa la dirección principal.
  }
  return null;
};

/**
 * @param {number} orderAmount Monto del carrito con el que evaluar el envío gratis.
 * @returns {{ready:boolean, pending:boolean, cost:number|null, threshold:number|null, methodName:string|null}}
 */
export default function useResolvedShipping(orderAmount) {
  const { state } = useContext(MyContext);
  const [data, setData] = useState(EMPTY);
  // Se incrementa cuando el checkout recalcula (cambio de dirección) para re-resolver.
  const [coordsVersion, setCoordsVersion] = useState(0);

  useEffect(() => {
    const bump = () => setCoordsVersion((v) => v + 1);
    window.addEventListener('shipping-method-updated', bump);
    window.addEventListener('storage', bump);
    // task-1488: al cambiar "comprar con/sin descuento" cambian las condiciones de
    // envío (solo retiro / envío gratis), así que hay que recalcular.
    window.addEventListener('user-discount-preference-updated', bump);
    return () => {
      window.removeEventListener('shipping-method-updated', bump);
      window.removeEventListener('storage', bump);
      window.removeEventListener('user-discount-preference-updated', bump);
    };
  }, []);

  const products = mapProductsForShipping(state?.cart || []);
  const amount = Number(orderAmount) || 0;
  // Firma estable: re-resuelve sólo cuando cambia el carrito, el monto o la dirección.
  const signature = `${JSON.stringify(products)}|${amount}|${coordsVersion}|${state?.user?.id ?? ''}`;

  useEffect(() => {
    const selected = readSelectedCoords();
    const main = state?.user?.addresses?.find((address) => address.main === 1) || null;
    const lat = selected?.lat ?? main?.lat;
    const lng = selected?.lng ?? main?.lng;

    if (!lat || !lng || products.length === 0) {
      setData(EMPTY);
      return undefined;
    }

    // Hay dirección y productos: la respuesta viene en camino. Marcamos pending para que
    // la barra no muestre nada basándose en el umbral global mientras tanto.
    setData((prev) => (prev.ready ? prev : PENDING));

    let cancelled = false;
    const timer = setTimeout(() => {
      axios
        .post(`${process.env.BASE_URL}/shipping/calculate`, {
          lat,
          lng,
          order_amount: amount,
          products,
          // task-1488: las restricciones de envío del convenio (solo retiro, sin
          // envío gratis) solo aplican si el cliente compra CON descuento.
          apply_user_discount: readApplyUserDiscount(),
          // task-1379: las reglas de productos pueden estar segmentadas (top
          // clientes, comuna, lista de correos). Sin identificar al cliente, el
          // backend sólo evalúa las de audiencia "todos" y la barra del sidecart
          // mostraría un envío distinto al que después cobra el checkout.
          user_email: getCustomerEmail(state?.user) ?? undefined,
          user_id: state?.user?.id ?? undefined,
        }, shippingRequestConfig())
        .then(({ data: response }) => {
          if (cancelled) return;
          const method = response?.shipping_method;
          if (!method) {
            setData(EMPTY);
            return;
          }
          setData({
            ready: true,
            pending: false,
            cost: Number(method.cost),
            threshold: response.free_shipping_threshold ?? null,
            methodName: method.shipping_method_name ?? null,
          });
        })
        .catch(() => {
          if (!cancelled) setData(EMPTY);
        });
    }, DEBOUNCE_MS);

    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [signature]);

  return data;
}
